import { useState, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, X } from 'lucide-react';
import {
  Button,
  Checkbox,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@scio3/ui';
import { cn } from '@scio3/ui';
import { SHARE_LINK_ACCESS_ROLES } from '@scio3/shared-types';
import type { ShareLink } from '@scio3/shared-types';
import { useShareLink } from '../hooks/useShareLink';
import { isEnterKey } from '@/lib/keyboard';
import { getGraphQLErrorCode } from '@/lib/graphql-error';
import { useRecaptcha, RECAPTCHA_DISABLED } from '@/features/auth/hooks/useRecaptcha';
import { useRecaptchaDialogFix } from '@/features/auth/hooks/useRecaptchaDialogFix';
import { RecaptchaV2Challenge } from '@/features/auth/components/RecaptchaV2Challenge';
import { toast } from 'sonner';

const CAPTCHA_ACTION = 'share_link_email';

type Props = {
  shareLink: ShareLink;
  objectName: string;
};

/** Basic email validation */
function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export function ShareLinkEmailInput({ shareLink }: Props) {
  const { t } = useTranslation();
  const { addEmails, addingEmails } = useShareLink(shareLink.objectId);

  const [emailInput, setEmailInput] = useState('');
  const [emails, setEmails] = useState<string[]>([]);
  const [role, setRole] = useState<string>(SHARE_LINK_ACCESS_ROLES.VIEWER);
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [bccMyself, setBccMyself] = useState(false);
  const [message, setMessage] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const [showDropdown, setShowDropdown] = useState(false);

  // reCAPTCHA — this mutation sends email, so it carries a token (see
  // ShareLinkEmailPayload in open-api). Same v3-invisible → v2-puzzle flow as
  // InviteUserDialog. The dialog fix is unconditional here: this component only ever
  // renders inside the (already open) Share Link dialog.
  const [challengeRequired, setChallengeRequired] = useState(false);
  useRecaptchaDialogFix(true);
  const { execute: executeCaptcha, renderV2Widget, onV2Solved, resetV2 } = useRecaptcha();

  // Existing emails already shared (from server)
  const existingEmails = useMemo(
    () => new Set((shareLink.securities ?? []).map((s) => s.userId.toLowerCase())),
    [shareLink.securities],
  );

  /** Parse input and add valid emails — supports comma, space, semicolon separators */
  const addEmailsFromInput = useCallback(
    (raw: string) => {
      const parts = raw.split(/[,;\s]+/).filter(Boolean);
      const newEmails: string[] = [];
      const duplicates: string[] = [];
      for (const part of parts) {
        const trimmed = part.trim().toLowerCase();
        if (!isValidEmail(trimmed)) continue;
        if (existingEmails.has(trimmed)) {
          duplicates.push(trimmed);
          continue;
        }
        if (emails.includes(trimmed) || newEmails.includes(trimmed)) continue;
        newEmails.push(trimmed);
      }
      if (duplicates.length > 0) {
        toast.error(
          t('{{emails}} already shared', {
            emails: duplicates.join(', '),
          }),
        );
      }
      if (newEmails.length > 0) {
        setEmails((prev) => [...prev, ...newEmails]);
      }
      setEmailInput('');
    },
    [emails, existingEmails, t],
  );

  const removeEmail = useCallback((email: string) => {
    setEmails((prev) => prev.filter((e) => e !== email));
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Enter, Tab, comma, semicolon — add email
      if (isEnterKey(e) || e.key === 'Tab' || e.key === ',' || e.key === ';') {
        if (emailInput.trim()) {
          e.preventDefault();
          addEmailsFromInput(emailInput);
        }
        return;
      }
      // Backspace on empty input — remove last chip
      if (e.key === 'Backspace' && !emailInput && emails.length > 0) {
        setEmails((prev) => prev.slice(0, -1));
      }
    },
    [emailInput, emails, addEmailsFromInput],
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      const pasted = e.clipboardData.getData('text');
      if (pasted.includes(',') || pasted.includes(';') || pasted.includes(' ')) {
        e.preventDefault();
        addEmailsFromInput(pasted);
      }
    },
    [addEmailsFromInput],
  );

  const handleBlur = useCallback(() => {
    if (emailInput.trim()) {
      addEmailsFromInput(emailInput);
    }
    setShowDropdown(false);
  }, [emailInput, addEmailsFromInput]);

  const doShare = useCallback(
    async (captcha: string, captchaVersion: 'v3' | 'v2') => {
      await addEmails(shareLink._id, {
        emails,
        role,
        notifyEmail,
        bccMyself,
        notifyMessage: message || undefined,
        captcha,
        captchaVersion,
        ...(captchaVersion === 'v3' ? { captchaAction: CAPTCHA_ACTION } : {}),
      });
      setEmails([]);
      setMessage('');
      toast.success(t('Shared successfully'));
    },
    [emails, role, notifyEmail, bccMyself, message, shareLink._id, addEmails, t],
  );

  const handleShare = useCallback(async () => {
    if (emails.length === 0) return;

    // Phase 1: v3 invisible (returns null when VITE_RECAPTCHA_DISABLE=true)
    const captchaToken = await executeCaptcha(CAPTCHA_ACTION);
    if (!captchaToken && !RECAPTCHA_DISABLED) {
      toast.error(t('reCAPTCHA verification failed. Please try again.'));
      return;
    }

    try {
      await doShare(captchaToken ?? '', 'v3');
    } catch (err: unknown) {
      if (getGraphQLErrorCode(err) === 'RECAPTCHA_CHALLENGE_REQUIRED') {
        // Phase 2: v2 image puzzle fallback
        setChallengeRequired(true);
        onV2Solved(async (v2Token: string) => {
          try {
            await doShare(v2Token, 'v2');
          } catch {
            toast.error(t('Failed to share'));
            resetV2();
          } finally {
            setChallengeRequired(false);
          }
        });
        return;
      }
      toast.error(t('Failed to share'));
    }
  }, [emails.length, executeCaptcha, doShare, onV2Solved, resetV2, t]);

  return (
    <div className="space-y-3">
      <Label>{t('Share via Email')}</Label>

      {/* Tag-style email input */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <div
            className={cn(
              'flex flex-wrap items-center gap-1 rounded-md border px-3 py-1.5 text-sm',
              'focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-1',
              'cursor-text',
            )}
            onClick={() => inputRef.current?.focus()}
          >
            {/* Email chips inline */}
            {emails.map((email) => (
              <span
                key={email}
                className="inline-flex items-center gap-1 rounded-sm bg-primary/10 px-2 py-0.5 text-xs"
              >
                {email}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeEmail(email);
                  }}
                  className="cursor-pointer text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={t('Remove {{email}}', { email })}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
            {/* Inline input */}
            <input
              ref={inputRef}
              type="email"
              placeholder={emails.length === 0 ? t('Enter email addresses') : ''}
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              onBlur={() => {
                // Delay blur to allow dropdown click
                setTimeout(() => handleBlur(), 150);
              }}
              onFocus={() => setShowDropdown(true)}
              aria-label={t('Email address')}
              autoComplete="off"
              className="min-w-[120px] flex-1 border-0 bg-transparent py-0.5 text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>

          {/* Dropdown suggestion */}
          {showDropdown &&
            emailInput.trim() &&
            isValidEmail(emailInput.trim()) &&
            (() => {
              const trimmedEmail = emailInput.trim().toLowerCase();
              const isExisting = existingEmails.has(trimmedEmail);
              const isDuplicate = emails.includes(trimmedEmail);
              return (
                <div className="absolute inset-x-0 top-full z-20 mt-1 rounded-md border bg-popover p-1 shadow-sm">
                  {isExisting || isDuplicate ? (
                    <div className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-sm text-muted-foreground">
                      <span className="font-medium">{trimmedEmail}</span>
                      <span>({t(isExisting ? 'Already shared' : 'Already added')})</span>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-sm hover:bg-accent transition-colors cursor-pointer text-left"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        addEmailsFromInput(emailInput);
                        setShowDropdown(true);
                      }}
                    >
                      <span className="font-medium">{trimmedEmail}</span>
                      <span className="text-muted-foreground">({t('Add email')})</span>
                    </button>
                  )}
                </div>
              );
            })()}
        </div>
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger className="w-28 shrink-0 cursor-pointer">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={SHARE_LINK_ACCESS_ROLES.EDITOR}>{t('Editor')}</SelectItem>
            <SelectItem value={SHARE_LINK_ACCESS_ROLES.VIEWER}>{t('Viewer')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Options */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Checkbox
            id="notify-email"
            checked={notifyEmail}
            onCheckedChange={(checked) => setNotifyEmail(checked === true)}
            aria-label={t('Notify Email')}
          />
          <Label htmlFor="notify-email" className="text-sm cursor-pointer">
            {t('Notify Email')}
          </Label>
        </div>

        <div className="flex items-center gap-2">
          <Checkbox
            id="bcc-myself"
            checked={bccMyself}
            onCheckedChange={(checked) => setBccMyself(checked === true)}
            aria-label={t('BCC Myself')}
          />
          <Label htmlFor="bcc-myself" className="text-sm cursor-pointer">
            {t('BCC Myself')}
          </Label>
        </div>
      </div>

      {/* Message */}
      {notifyEmail && (
        <div className="space-y-2">
          <Label htmlFor="share-message">{t('Message')}</Label>
          <Textarea
            id="share-message"
            placeholder={t('Add a message (optional)')}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={2}
            className="resize-none"
          />
        </div>
      )}

      {/* reCAPTCHA v2 fallback — only rendered when v3 scoring asks for a puzzle */}
      <RecaptchaV2Challenge visible={challengeRequired} renderV2Widget={renderV2Widget} />

      {/* Share button */}
      <Button
        onClick={handleShare}
        disabled={emails.length === 0 || addingEmails || challengeRequired}
        className="cursor-pointer"
        size="sm"
      >
        {addingEmails && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {t('Share')}
      </Button>
    </div>
  );
}
